const hasValue = (value) => value !== undefined && value !== null && String(value).trim() !== "";

export function isTriggerAnswerValid(question, trigger) {
  return (
    hasValue(trigger.answerValue) &&
    (question.choices || []).some(
      (choice) => hasValue(choice.value) && String(choice.value) === String(trigger.answerValue)
    )
  );
}

/** Read-only checks against the current editor draft, including legacy name-only triggers. */
export function validateSurvey(pages = []) {
  const issues = [];
  const targets = [];
  pages.forEach((page, pageIndex) => {
    const parent = { model: page, type: "page", pageIndex };
    targets.push(parent);
    (page.questions || []).forEach((question, questionIndex) => {
      targets.push({ model: question, type: "question", pageIndex, questionIndex, parent });
    });
  });
  const add = (category, severity, code, locations, values = {}) =>
    issues.push({ category, severity, code, locations, values });

  // Keys and internal names share a namespace for trigger visibility resolution.
  ["key", "name"].forEach((field) => {
    const groups = new Map();
    targets.forEach((target) => {
      const value = target.model[field];
      if (!hasValue(value)) return;
      if (!groups.has(value)) groups.set(value, []);
      groups.get(value).push(target);
    });
    groups.forEach((locations, value) => {
      if (locations.length > 1) {
        add("ids", "error", field === "key" ? "duplicateKey" : "duplicateName", locations, { value });
      }
    });
  });

  const checkElementKeys = (container, question, elementPath = []) => {
    [
      ["columns", "column", "missingColumnKey"],
      ["rows", "row", "missingRowKey"],
      ["choices", "option", "missingOptionKey"]
    ].forEach(([field, type, code]) => {
      (container[field] || []).forEach((element, index) => {
        const path = [...elementPath, { type, index, model: element }];
        if (!hasValue(element.key)) {
          add("ids", "error", code, [{ ...question, elementPath: path }]);
        }
        checkElementKeys(element, question, path);
      });
    });
  };
  targets
    .filter((target) => target.type === "question")
    .forEach((question) => {
      checkElementKeys(question.model, question);
    });

  const edges = [];
  let triggerCount = 0;
  targets
    .filter((target) => target.type === "question")
    .forEach((source) => {
      const question = source.model;
      (question.triggers || []).forEach((trigger, triggerIndex) => {
        triggerCount++;
        const location = { ...source, triggerIndex };
        let locations = [location];
        const report = (severity, code, values) => add("triggers", severity, code, locations, values);
        if (!question.choices?.length || ["matrixSingleChoice", "matrixMultipleChoice"].includes(question.type)) {
          report("error", "unsupportedSource");
        }
        if (!isTriggerAnswerValid(question, trigger)) {
          report("error", "invalidAnswer", { value: trigger.answerValue ?? "–" });
        }
        if ((trigger.compare && trigger.compare !== "EQ") || (trigger.action && trigger.action !== "ACTIVATE")) {
          report("error", "unsupportedOperation");
        }
        const byKey = hasValue(trigger.targetKey)
          ? targets.filter((target) => target.model.key === trigger.targetKey)
          : [];
        const byName = hasValue(trigger.targetName)
          ? targets.filter((target) => target.model.name === trigger.targetName)
          : [];
        const matches = [...new Set([...byKey, ...byName])];
        if (!matches.length) {
          report("error", "missingTarget", { value: trigger.targetKey || trigger.targetName || "–" });
          return;
        }
        if (matches.length > 1) {
          add("triggers", "error", "ambiguousTarget", [location, ...matches]);
          return;
        }
        const target = matches[0];
        locations = [location, target];
        if ((hasValue(trigger.targetKey) && !byKey.length) || (hasValue(trigger.targetName) && !byName.length)) {
          report("warning", "staleReference");
        }
        if (trigger.targetType && trigger.targetType !== target.type) report("error", "wrongTargetType");
        if (target === source || target === source.parent) {
          report("error", "selfReference");
          return;
        }
        if (target.model.visible !== false) report("warning", "visibleTarget");
        if (
          target.pageIndex < source.pageIndex ||
          (target.pageIndex === source.pageIndex &&
            target.type === "question" &&
            target.questionIndex < source.questionIndex)
        ) {
          report("warning", "backwardTarget");
        }
        edges.push({ source, target, location });
      });
    });

  // Activating a page makes its questions available, so include that dependency in cycle checks.
  const graph = new Map(targets.map((target) => [target, []]));
  targets.filter((target) => target.parent).forEach((target) => graph.get(target.parent).push(target));
  edges.forEach(({ source, target }) => graph.get(source).push(target));
  const reaches = (start, goal) => {
    const seen = new Set();
    const pending = [start];
    while (pending.length) {
      const current = pending.pop();
      if (current === goal) return true;
      if (seen.has(current)) continue;
      seen.add(current);
      pending.push(...graph.get(current));
    }
    return false;
  };
  edges.forEach(({ source, target, location }) => {
    if (reaches(target, source)) add("triggers", "warning", "cycle", [location, target]);
  });

  return {
    issues,
    pageCount: pages.length,
    questionCount: targets.length - pages.length,
    triggerCount
  };
}
